import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import _ from 'lodash';

import { routes, TEMPLATE_TYPE } from 'src/app/constants';
import { Template } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';

import { ComposePage } from '../compose/compose.page';
import { BLOCK_TYPES, htmlToBlocks, renderBlocks } from '../slack-blocks/common';
import { UnlayerWrapperComponent } from '../unlayer-wrapper/unlayer-wrapper.component';

import { TemplateOptionsPage } from './template-options/template-options.page';

@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
})
export class TemplateEditorComponent implements OnInit {
  @Input() communityId: string | null = null;
  @ViewChild(UnlayerWrapperComponent) emailEditor: UnlayerWrapperComponent;

  blocks: any[] = [];
  template?: Partial<Template>;
  isReadOnly = false;
  isFetchingMembers = false;
  isLoading = false;
  warning: string;

  constructor(
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private navCtrl: NavController,
    public authService: AuthService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private customersService: CustomersService,
  ) {}

  async ngOnInit() {
    const templateId = this.route.snapshot.paramMap.get('id');
    const sampleId = this.route.snapshot.paramMap.get('sampleId');
    const isNew = this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path === 'new';
    const templateType = this.route.snapshot.queryParams['type'];

    if (templateId) {
      // Loading existing template
      await this.loadTemplate(templateId);
    } else if (sampleId) {
      // Loading sample template to copy
      await this.loadSampleTemplate(sampleId);
    } else if (isNew && templateType) {
      // Creating new template
      this.template = {
        name: templateType === TEMPLATE_TYPE.EMAIL ? 'My email template' : 'My Slack template',
        isDiscoverable: true,
        type: templateType,
        projectId: this.authService.selectedProject?.id,
        tenantId: this.authService.tenantId,
      };

      if (!this.authService.selectedProject) {
        // Need to show modal for project selection
        const modal = await this.modalCtrl.create({
          component: TemplateOptionsPage,
          componentProps: {
            template: this.template,
            isNewTemplate: true,
            callback: (template) => {
              modal.dismiss();
              if (template) {
                this.template.name = template.name;
                this.template.isDiscoverable = template.isDiscoverable;
                this.template.projectId = template.projectId;
                this.template.project = template.project;
              } else {
                // User cancelled, go back
                this.goBack();
              }
            },
          },
          cssClass: 'modal-content-only modal-skinny',
        });
        await modal.present();
      }
    }
  }

  async loadTemplate(templateId: string) {
    this.isLoading = true;
    try {
      const template: Template = await this.apiService.getPromise(`/templates/${templateId}`);
      this.template = template;
      this.isReadOnly = !template.tenantId;

      if (template.projectId) {
        const project: any = await this.apiService.getPromise(`/projects/${template.projectId}`);
        this.template.project = project;
      }

      if (template.type === TEMPLATE_TYPE.SLACK) {
        this.blocks = htmlToBlocks(template.html ?? 'Sample content here');

        if (!this.blocks.length) {
          this.blocks.push({
            type: BLOCK_TYPES.RICH_TEXT,
            htmlContent: 'Sample content here',
          });
        }
      }
    } catch (err) {
      this.msgSrvc.showError(err);
      this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  async loadSampleTemplate(sampleId: string) {
    this.isLoading = true;
    try {
      const samples: any = await this.apiService.getPromise(`/template-samples`);
      const sample = samples.rows.find((s: Template) => s.id === sampleId);

      if (!sample) {
        this.msgSrvc.show('Sample template not found');
        this.goBack();
        return;
      }

      this.template = {
        ...sample,
        name: `Copy of ${sample.name}`,
        tenantId: this.authService.tenantId,
        projectId: this.authService.selectedProject?.id,
        id: undefined,
      };

      this.isReadOnly = true; // Sample templates are read-only until saved

      this.analyticsService.trackEvent('Template Editor', `Select Unthread-provided template`);

      if (this.template.type === TEMPLATE_TYPE.SLACK) {
        this.blocks = htmlToBlocks(this.template.html ?? 'Sample content here');

        if (!this.blocks.length) {
          this.blocks.push({
            type: BLOCK_TYPES.RICH_TEXT,
            htmlContent: 'Sample content here',
          });
        }
      }
    } catch (err) {
      this.msgSrvc.showError(err);
      this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  editorLoaded() {
    setTimeout(() => {
      if (this.template && !_.isEmpty(this.template.design)) {
        this.emailEditor.editor.loadDesign(this.template.design);
      } else {
        this.emailEditor.editor.loadBlank({
          backgroundColor: '#fff',
          contentWidth: '650px',
        });
        // Hacky shit because this editor sucks
        // If we dont' have this timeout, it will not work
        setTimeout(() => {
          if (!this.template || !this.template.id) {
            this.emailEditor.editor.setBodyValues({
              backgroundColor: '#fff',
              contentWidth: '650px',
            });
          }
        }, 3000);
      }
    }, 500);

    this.emailEditor?.editor?.addEventListener('design:updated', (data) => {
      // This is called when the editor changes
      // We've had issues with people embedding iFrames, which don't appear properly in email
      // This code is semi-problematic because it will clear out the warning when you edit a different HTML element,
      // but it's good enough for now
      if (
        _.get(data, 'item.type') === 'html' &&
        _.get(data, 'item.values.html').toLowerCase().indexOf('<iframe') >= 0
      ) {
        this.warning = 'You have embedded an iFrame as HTML. This will not appear when sent via email.';
      } else {
        this.warning = '';
      }
    });
  }

  async editTemplate() {
    if (this.isReadOnly) {
      return;
    }

    this.presentEditModalWithProps({
      template: this.template,
    });
  }

  async clone() {
    this.presentEditModalWithProps({
      template: this.template,
      clone: true,
    });

    this.analyticsService.trackEvent(
      'Template Editor',
      `Clone ${this.template?.tenantId ? 'existing' : 'Unthread-provided'} template`,
    );
  }

  async showWarnings() {
    const alert = await this.alertCtrl.create({
      header: 'Warnings',
      subHeader: this.warning,
      buttons: [
        {
          text: 'OK',
        },
      ],
    });
    alert.present();
  }

  async sendEmail() {
    this.analyticsService.trackEvent('Template Editor', 'Send email from template editor');

    // Get member list to populate
    let emails = [];

    if (this.communityId) {
      this.isFetchingMembers = true;

      const members = await this.customersService.getFollowers(this.communityId);

      emails = members.map((member) => {
        return member.user?.email;
      });

      this.isFetchingMembers = false;
    }

    this.emailEditor.editor.exportHtml(async (data) => {
      const { html } = data;
      const modal = await this.modalCtrl.create({
        component: ComposePage,
        componentProps: {
          communityId: this.communityId,
          emails,
          // Kinda hacky way of pretending this is an Outbound object
          draft: {
            body: html,
            communityId: this.communityId,
            recipients: [],
            metadata: {
              templateId: this.template.id,
            },
          },
        },
        backdropDismiss: false,
        cssClass: 'modal-large',
      });
      modal.present();
    });
  }

  async sendSlack() {
    this.analyticsService.trackEvent('Template Editor', 'Send Slack from template editor');

    this.navCtrl.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: {
        communityId: this.communityId,
        templateId: this.template.id,
      },
    });
  }

  async presentEditModalWithProps(props: any) {
    const modal = await this.modalCtrl.create({
      component: TemplateOptionsPage,
      componentProps: {
        ...props,
        communityId: this.communityId,
        callback: async (template) => {
          modal.dismiss();
          if (template) {
            this.template.name = template.name;
            this.template.isDiscoverable = template.isDiscoverable;
            this.template.projectId = template.projectId;
            this.template.project = template.project;

            if (props.clone) {
              // After a clone, we'll want to save immediately because the user has entered a name and design
              await this.save();
            }
          } else if (props.template?.id) {
            // Template was deleted, go back
            this.goBack();
          }
        },
      },
      cssClass: 'modal-content-only modal-skinny',
    });
    modal.present();
  }

  async checkIfShouldGoBack() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to go back?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: () => {
            this.goBack();
          },
        },
      ],
    });

    alert.present();
  }

  async save() {
    if (this.template.type === TEMPLATE_TYPE.SLACK) {
      if (this.template.id) {
        this.updateTemplateToServer(this.blocks, null, renderBlocks(this.blocks));
      } else {
        this.analyticsService.trackEvent('Template editor', 'Save new Slack template');
        await this.saveNewTemplateToServer(this.blocks, null, renderBlocks(this.blocks));
      }
    } else if (this.template.type === TEMPLATE_TYPE.EMAIL) {
      this.emailEditor.editor.exportHtml(async (data) => {
        const { chunks, design, html } = data;

        const cleanedHtml = html.replace('<head>', '').replace('</head>', '');
        this.isLoading = true;

        if (this.template.id) {
          this.updateTemplateToServer(chunks, design, cleanedHtml);
        } else {
          this.analyticsService.trackEvent('Template editor', 'Save new email template');
          await this.saveNewTemplateToServer(chunks, design, cleanedHtml);
        }
      });
    }
  }

  async updateTemplateToServer(chunks: any, design = null, html = null) {
    this.isLoading = true;

    try {
      await this.apiService.putPromise(`/templates/${this.template.id}`, {
        type: this.template.type,
        chunks,
        design,
        html,
      });

      this.msgSrvc.show('✔ Nice! Saved.');
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async saveNewTemplateToServer(chunks: any, design = null, html = null) {
    this.isLoading = true;

    try {
      const result = await this.apiService.postPromise(`/templates`, {
        name: this.template.name,
        type: this.template.type,
        isDiscoverable: this.template.isDiscoverable,
        chunks,
        design,
        html,
        communityId: this.communityId || null,
        projectId: this.template.projectId,
      });

      const project = this.template.project;

      this.template = result;
      this.template.project = project;

      this.isReadOnly = false;
      this.msgSrvc.show('✔ Nice! New template saved.');

      // Update URL to reflect the new template ID
      this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.OUTBOUND}/templates/edit/${this.template.id}`);
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  goBack() {
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.OUTBOUND}/templates`);
  }

  unlayerOptions() {
    return {
      fonts: {
        showCustomFonts: false,
        customFonts: [
          {
            label: 'Arial',
            value: 'arial,helvetica,sans-serif',
          },
          {
            label: 'Arial Black',
            value: 'arial black,avant garde,arial',
          },
          {
            label: 'Book Antiqua',
            value: 'book antiqua,palatino',
          },
          {
            label: 'Comic Sans MS',
            value: 'comic sans ms,sans-serif',
          },
          {
            label: 'Courier New',
            value: 'courier new,courier',
          },
          {
            label: 'Georgia',
            value: 'georgia,palatino',
          },
          {
            label: 'Helvetica',
            value: 'helvetica,sans-serif',
          },
          {
            label: 'Impact',
            value: 'impact,chicago',
          },
          {
            label: 'Tahoma',
            value: 'tahoma,arial,helvetica,sans-serif',
          },
          {
            label: 'Times New Roman',
            value: 'times new roman,times',
          },
          {
            label: 'Trebuchet MS',
            value: 'trebuchet ms,geneva',
          },
          {
            label: 'Verdana',
            value: 'verdana,geneva',
          },
        ],
      },
    };
  }
}
